import {styleAtom} from '../emotion/emotion';
import {type StyledAtom} from '../emotion/emotion.types';
import {animations} from './animations';
import {tokens} from '@packages/utilities/styled-tokens';

/**
 * Style atom creator for mobile devices (iPhone, iPad, Android, etc...).
 *
 * @see https://dev.opera.com/articles/media-features/
 * @see https://css-tricks.com/touch-devices-not-judged-size/
 */
export const createMobileStyleAtom: (styles?: any) => StyledAtom = (
  styles: any = {}
) =>
  styleAtom({
    '@media (pointer:coarse)': styles,
  });

/**
 * For mobile devices, a minimum of 16px font-size is required
 */
export const mobileTextControlStyleAtom: StyledAtom = createMobileStyleAtom({
  fontSize: `max(1rem, 16px)`,
});

/**
 * Style atom to customize the default browser scrollbars.
 */
export const customScrollbarAtom: StyledAtom = styleAtom(
  {
    '&::-webkit-scrollbar': {
      width: 7,
    },

    '&::-webkit-scrollbar-track': {
      borderRadius: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },

    '&::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      borderColor: 'transparent',
      borderStyle: 'solid',
      backgroundClip: 'content-box',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  },
  {
    variants: {
      scrollbarSize: {
        large: {
          '&::-webkit-scrollbar': {
            width: 11,
          },
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 12,
        },
      },
    },
  }
);

export const fontSizeAtom: StyledAtom = styleAtom({
  fontSize: tokens.typography.fontSize.body,
});

/**
 * Resets UA styles associated with `<button>` elements. Unless specified,
 * resets are necessary in all browsers
 */

export const buttonResetAtom: StyledAtom = styleAtom({
  border: 'none',
  background: 'none',
  display: 'inline',
  textAlign: 'left',
  padding: 0,
  margin: 0, // webkit
});

/**
 * Style atom to safely remove animations for users that prefer
 * reduced motions on their devices.
 */
export const reducedMotionAtom: StyledAtom = styleAtom({
  '@media (prefers-reduced-motion)': {
    animationDelay: `0ms !important`,
    animationDuration: `${animations.durationDisabled} !important`,
    transitionDelay: `0ms !important`,
    transitionDuration: `${animations.durationDisabled} !important`,
  },
});

/**
 * Style atom to hide CSS outlines in an accessible manner.
 */
export const hideOutlineAtom: StyledAtom = styleAtom({
  '&:focus': {
    outline: '2px solid transparent',
  },
});
